import React from "react";

import styled from "styled-components";

import { SearchField, SpinLoader } from "@/components/atoms";
import { StandardLayout } from "@/components/layouts";
import { handleHighlight } from "@/core/html";
import { StyledListEmpty } from "@/styles/styled";

const StyledSearchList = styled.div`
    margin: 2rem 0;

    .spinner {
        width: 100%;
        margin-top: 2rem;
    }
`;

type SearchListProps<ObjectType> = {
    pageTitle: string;
    textSearch: string;
    placeholder: string;
    isFetching: boolean;
    emptyListMessage: string;
    items: ObjectType[];
    setTextSearch: React.Dispatch<React.SetStateAction<string>>;
    renderItem?: (item: ObjectType) => React.ReactElement;
    renderTable: (items: ObjectType[]) => React.ReactElement;
    openLinkedDocuments?: () => void;
    dataTestId?: string;
    hideGlobalFilters?: boolean;
    tableMode: boolean;
};

const SearchList = <ObjectType,>({
    items,
    pageTitle,
    isFetching,
    textSearch,
    placeholder,
    emptyListMessage,
    renderItem,
    renderTable,
    setTextSearch,
    openLinkedDocuments,
    hideGlobalFilters = false,
    tableMode,
}: SearchListProps<ObjectType>) => {
    const handleSearch = (searchWord) => {
        if (searchWord === textSearch) return;
        setTextSearch(searchWord);
        handleHighlight(searchWord);
    };

    const getResults = () => {
        if (isFetching) {
            return <SpinLoader />;
        }

        if (items.length > 0) {
            if (tableMode) {
                return renderTable(items);
            }

            return items.map((item) => renderItem(item));
        }

        if (items.length === 0) {
            return <StyledListEmpty data-testid="empty-search-list">{emptyListMessage}</StyledListEmpty>;
        }

        return null;
    };

    return (
        <StandardLayout
            title={pageTitle}
            showGlobalFilter={!hideGlobalFilters}
            onOpenLinkedDocuments={openLinkedDocuments}
        >
            <div>
                <SearchField placeholder={placeholder} onSearch={handleSearch} />
                <StyledSearchList className="context" data-testid="search-list">
                    {getResults()}
                </StyledSearchList>
            </div>
        </StandardLayout>
    );
};

export default SearchList;
